import type { WorkerPosition } from "./positions";

/**
 * Skill catalog F&B-specific, grouped by primary position.
 * Worker chọn các skill từ flat list; employer khi post job filter theo position
 * sẽ thấy các skill liên quan suggest đầu danh sách.
 */
export const SKILLS_BY_POSITION: Record<WorkerPosition, readonly string[]> = {
  WAITER: [
    "BUNG_BE",
    "ORDER_POS",
    "PHUC_VU_TIEC",
    "PHUC_VU_LAU_NUONG",
    "WINE_KNOWLEDGE",
    "FINE_DINING_ETIQUETTE",
    "MENU_KNOWLEDGE",
  ],
  FOOD_RUNNER: ["BUNG_BE", "ORDER_POS", "MENU_KNOWLEDGE"],
  HOST: [
    "CUSTOMER_GREETING",
    "RESERVATION_MGMT",
    "EN_GUEST_COMM",
    "PHONE_HANDLING",
  ],
  BARTENDER: [
    "COCKTAIL_IBA",
    "MOCKTAIL",
    "FLAIR_BARTENDING",
    "BAR_INVENTORY",
    "WINE_KNOWLEDGE",
    "BEER_KNOWLEDGE",
  ],
  BAR_BACK: ["BAR_INVENTORY", "MOCKTAIL"],
  BARISTA: [
    "LATTE_ART",
    "ESPRESSO_MACHINE",
    "COFFEE_DRIP",
    "POUR_OVER",
    "COFFEE_BEAN_KNOWLEDGE",
    "MILK_FROTHING",
  ],
  BEVERAGE: [
    "MILK_TEA",
    "SMOOTHIE",
    "JUICE",
    "SODA_MIX",
    "BUBBLE_TEA",
    "ICE_BLENDED",
  ],
  HEAD_CHEF: [
    "CUISINE_VIET",
    "CUISINE_ASIAN",
    "CUISINE_WESTERN",
    "CUISINE_JAPANESE",
    "CUISINE_KOREAN",
    "CUISINE_THAI",
    "CUISINE_CHINESE",
    "PASTRY",
    "BREAD",
    "KITCHEN_INVENTORY",
    "MENU_DEVELOPMENT",
    "FOOD_COST_CONTROL",
  ],
  KITCHEN_SUPERVISOR: [
    "KITCHEN_INVENTORY",
    "MENU_DEVELOPMENT",
    "FOOD_COST_CONTROL",
    "QUALITY_CONTROL",
    "SCHEDULING",
    "STAFF_TRAINING",
    "SOP_ENFORCEMENT",
  ],
  HOT_KITCHEN: [
    "CUISINE_VIET",
    "CUISINE_ASIAN",
    "CUISINE_WESTERN",
    "GRILL_BBQ",
    "FRIED_DISHES",
    "KNIFE_SKILLS",
    "PREP_WORK",
  ],
  HOT_KITCHEN_HELPER: [
    "KNIFE_SKILLS",
    "PREP_WORK",
    "VEGETABLE_PREP",
    "GRILL_BBQ",
  ],
  COLD_KITCHEN: [
    "CUISINE_WESTERN",
    "CUISINE_JAPANESE",
    "KNIFE_SKILLS",
    "PREP_WORK",
    "VEGETABLE_PREP",
  ],
  COLD_KITCHEN_HELPER: ["KNIFE_SKILLS", "PREP_WORK", "VEGETABLE_PREP"],
  COOK: [
    "CUISINE_VIET",
    "CUISINE_ASIAN",
    "CUISINE_WESTERN",
    "CUISINE_JAPANESE",
    "CUISINE_KOREAN",
    "CUISINE_THAI",
    "KNIFE_SKILLS",
    "PREP_WORK",
    "GRILL_BBQ",
    "FRIED_DISHES",
  ],
  KITCHEN_STAFF: [
    "CUISINE_VIET",
    "CUISINE_ASIAN",
    "KNIFE_SKILLS",
    "PREP_WORK",
    "FRIED_DISHES",
  ],
  KITCHEN_HELPER: [
    "KNIFE_SKILLS",
    "PREP_WORK",
    "DISHWASHING",
    "VEGETABLE_PREP",
  ],
  BAKER: ["PASTRY", "BREAD", "FOOD_COST_CONTROL"],
  DISHWASHER: ["DISHWASHING", "CHEMICAL_HANDLING"],
  CASHIER: [
    "POS_IPOS",
    "POS_KIOTVIET",
    "POS_SAPO",
    "POS_POSAPP",
    "POS_GENERIC",
    "CASH_HANDLING",
    "CUSTOMER_SERVICE",
    "BANK_TRANSFER_QR",
  ],
  RECEPTIONIST: [
    "POS_GENERIC",
    "CUSTOMER_GREETING",
    "RESERVATION_MGMT",
    "EN_GUEST_COMM",
    "PHONE_HANDLING",
  ],
  DELIVERY: ["MOTORBIKE_LICENSE", "AREA_KNOWLEDGE", "DELIVERY_APP_FAMILIAR"],
  SECURITY: ["CONFLICT_RESOLUTION", "NIGHT_SHIFT_OK", "CCTV_MONITORING"],
  PARKING: ["NIGHT_SHIFT_OK", "CONFLICT_RESOLUTION"],
  CLEANER: ["DEEP_CLEANING", "CHEMICAL_HANDLING", "FLOOR_POLISHING"],
  STOCK_KEEPER: ["INVENTORY", "KITCHEN_INVENTORY", "COST_CONTROL"],
  MANAGER: [
    "STAFF_TRAINING",
    "SCHEDULING",
    "COST_CONTROL",
    "INVENTORY",
    "COMPLAINT_HANDLING",
    "PNL_BASIC",
    "VENDOR_MGMT",
    "MARKETING_BASIC",
  ],
  SHIFT_LEADER: [
    "SHIFT_HANDOVER",
    "PEAK_HOUR_MGMT",
    "STAFF_TRAINING",
    "COMPLAINT_HANDLING",
  ],
  SUPERVISOR: ["QUALITY_CONTROL", "STAFF_EVALUATION", "SOP_ENFORCEMENT"],
  TRAINER: ["CURRICULUM_DESIGN", "COACHING", "ONBOARDING"],
  MARKETING: ["MARKETING_BASIC"],
  OTHER: [],
};

/** Flat list of all unique skills (cho worker fill profile + employer search). */
export const ALL_SKILLS: readonly string[] = Array.from(
  new Set(Object.values(SKILLS_BY_POSITION).flat()),
);

export const SKILL_LABEL_VI: Record<string, string> = {
  // Waiter
  BUNG_BE: "Bưng bê",
  ORDER_POS: "Order POS",
  PHUC_VU_TIEC: "Phục vụ tiệc",
  PHUC_VU_LAU_NUONG: "Phục vụ lẩu nướng",
  WINE_KNOWLEDGE: "Hiểu biết về rượu vang",
  FINE_DINING_ETIQUETTE: "Etiquette nhà hàng cao cấp",
  MENU_KNOWLEDGE: "Thuộc thực đơn",
  // Bartender
  COCKTAIL_IBA: "Cocktail (IBA classic)",
  MOCKTAIL: "Mocktail",
  FLAIR_BARTENDING: "Flair bartending",
  BAR_INVENTORY: "Quản lý kho bar",
  BEER_KNOWLEDGE: "Hiểu biết về bia",
  // Barista
  LATTE_ART: "Latte art",
  ESPRESSO_MACHINE: "Máy espresso",
  COFFEE_DRIP: "Cà phê drip / phin",
  POUR_OVER: "Pour over",
  COFFEE_BEAN_KNOWLEDGE: "Hiểu biết về hạt cà phê",
  MILK_FROTHING: "Đánh sữa",
  // Beverage
  MILK_TEA: "Pha trà sữa",
  SMOOTHIE: "Pha sinh tố",
  JUICE: "Pha nước ép",
  SODA_MIX: "Pha soda",
  BUBBLE_TEA: "Trà sữa trân châu",
  ICE_BLENDED: "Đá xay",
  // Cuisine
  CUISINE_VIET: "Bếp Việt",
  CUISINE_ASIAN: "Bếp Á",
  CUISINE_WESTERN: "Bếp Âu",
  CUISINE_JAPANESE: "Bếp Nhật",
  CUISINE_KOREAN: "Bếp Hàn",
  CUISINE_THAI: "Bếp Thái",
  CUISINE_CHINESE: "Bếp Trung",
  PASTRY: "Làm bánh ngọt",
  BREAD: "Làm bánh mì",
  KITCHEN_INVENTORY: "Quản lý kho bếp",
  MENU_DEVELOPMENT: "Phát triển thực đơn",
  FOOD_COST_CONTROL: "Kiểm soát chi phí thực phẩm",
  KNIFE_SKILLS: "Kỹ năng dao",
  PREP_WORK: "Sơ chế",
  GRILL_BBQ: "Nướng / BBQ",
  FRIED_DISHES: "Món chiên",
  DISHWASHING: "Rửa chén",
  VEGETABLE_PREP: "Sơ chế rau củ",
  // Cashier
  POS_IPOS: "POS iPOS",
  POS_KIOTVIET: "POS KiotViet",
  POS_SAPO: "POS Sapo",
  POS_POSAPP: "POS Posapp",
  POS_GENERIC: "POS (chung)",
  CASH_HANDLING: "Xử lý tiền mặt",
  CUSTOMER_SERVICE: "Chăm sóc khách hàng",
  BANK_TRANSFER_QR: "Chuyển khoản QR",
  // Receptionist
  CUSTOMER_GREETING: "Đón khách",
  RESERVATION_MGMT: "Quản lý đặt bàn",
  EN_GUEST_COMM: "Tiếng Anh giao tiếp khách",
  PHONE_HANDLING: "Xử lý điện thoại",
  // Delivery
  MOTORBIKE_LICENSE: "Có bằng lái xe máy",
  AREA_KNOWLEDGE: "Thuộc đường khu vực",
  DELIVERY_APP_FAMILIAR: "Quen dùng app giao hàng",
  // Security
  CONFLICT_RESOLUTION: "Xử lý xung đột",
  NIGHT_SHIFT_OK: "Có thể trực đêm",
  CCTV_MONITORING: "Giám sát CCTV",
  // Cleaner
  DEEP_CLEANING: "Vệ sinh sâu",
  CHEMICAL_HANDLING: "Sử dụng hóa chất tẩy rửa",
  FLOOR_POLISHING: "Đánh bóng sàn",
  // Manager
  STAFF_TRAINING: "Đào tạo nhân viên",
  SCHEDULING: "Sắp xếp lịch",
  COST_CONTROL: "Kiểm soát chi phí",
  INVENTORY: "Quản lý kho",
  COMPLAINT_HANDLING: "Xử lý phàn nàn",
  PNL_BASIC: "P&L cơ bản",
  VENDOR_MGMT: "Quản lý nhà cung cấp",
  MARKETING_BASIC: "Marketing cơ bản",
  SHIFT_HANDOVER: "Bàn giao ca",
  PEAK_HOUR_MGMT: "Quản lý giờ cao điểm",
  QUALITY_CONTROL: "Kiểm soát chất lượng",
  STAFF_EVALUATION: "Đánh giá nhân viên",
  SOP_ENFORCEMENT: "Áp dụng SOP",
  CURRICULUM_DESIGN: "Thiết kế chương trình đào tạo",
  COACHING: "Huấn luyện",
  ONBOARDING: "Onboard nhân viên mới",
};

export function suggestSkillsForPosition(
  position: WorkerPosition,
): readonly string[] {
  return SKILLS_BY_POSITION[position] ?? [];
}
